import { config } from './config';

export function debugLog(...args: any[]): void {
  if (localStorage.getItem('pixelDebugLog') === 'true') {
    console.log.apply(null, args);
  }
}

/**
 * Generates a random ID string consisting of upper/lower case letters, and numbers.
 * @param {number} length How long the ID should be (how many characters).
 * @returns {string} The randomly generated ID.
 */
export function generateID(length: number = 32): string {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export async function fetchSiteInformation(siteId: string) {
  try {
    const response = await fetch(`${config.HIO_API_ENDPOINT}/api/booking/getSiteInfo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        siteId,
      }),
    });

    const jsonResponse = await response.json();
    return jsonResponse.siteInfo;
  } catch (e) {
    console.log(e);
    return {};
  }
}

export function getCookie(
  cookieName: string,
  convertDateString = true,
): string | number | boolean | Date {
  let name: string[] | null;
  // The regular expression tries to find the pattern 'cookieName=value;'
  // If it finds it, name will be an array where the first element contains the matched string
  // Then split the string by '=' and ';' to form an array where the first element is the name
  // And the second element is the value
  const cookieValue: string | null = (name = (`${document.cookie};`).match(new RegExp(`${cookieName}=.*;`))) && name[0].split(/=|;/)[1];
  if (cookieValue === null) {
    if (cookieName.endsWith('SetDate')) {
      return null;
    }
    return '';
  }
  if (cookieValue === 'true') {
    return true;
  }
  if (cookieValue === 'false') {
    return false;
  }
  if (cookieName.endsWith('SetDate') && convertDateString) {
    return new Date(cookieValue);
  }
  if (!Number.isNaN(Number(cookieValue))) {
    return Number(cookieValue);
  }
  return cookieValue;
}

// Sourced and modified from https://stackoverflow.com/a/58650077/1995444
/**
 * Gets the browser name
 * @returns {string} The browser name
 */
export function getBrowserName(): 'chrome' | 'edge' | 'chromium based edge' | 'opera' | 'ie' | 'firefox' | 'safari' | 'other' {
  const agent = window.navigator.userAgent.toLowerCase();
  // @ts-ignore
  // eslint-disable-next-line no-nested-ternary
  const browser = agent.indexOf('edge') > -1 ? 'edge' : agent.indexOf('edg') > -1 ? 'chromium based edge' : agent.indexOf('opr') > -1 && window.opr ? 'opera' : agent.indexOf('chrome') > -1 && window.chrome ? 'chrome' : agent.indexOf('trident') > -1 ? 'ie' : agent.indexOf('firefox') > -1 ? 'firefox' : agent.indexOf('safari') > -1 ? 'safari' : 'other';
  return browser;
}

function getCookieExpirationDate(): string {
  const browserType = getBrowserName();
  const date = new Date();
  let days = 30;
  if (browserType === 'safari') {
    days = 7;
  }
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000)); // days converted to milliseconds
  const expires = `expires=${date.toUTCString()}`;
  return expires;
}

export function setCookie(
  cookieName: string,
  value: string,
): void {
  document.cookie = `${cookieName}=${value}; ${getCookieExpirationDate()}; path=/; SameSite=None; Secure;`;
}

function getURLHostname(url: string): string {
  try {
    const { hostname } = new URL(url);
    return hostname;
  } catch (e) {
    throw new Error(`Invalid URL: ${url}`);
  }
}

function handlePostMessage(event: MessageEvent): void {
  if (event.data.clearCookies) {
    document.cookie = 'hovrEngaged=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }
}

export function getTopWindowURL(): string {
  const topWindow = window.location !== window.parent.location
    ? document.referrer
    : document.location.origin;
  if (topWindow === '') {
    return window.parent.location.origin;
  }
  return topWindow;
}

/**
 * Listen from messages from hosts
 */
export function initPostMessageListener() {
  window.addEventListener(
    'message',
    async (event) => {
      if (getURLHostname(getTopWindowURL()) === getURLHostname(event.origin)) {
        handlePostMessage(event);
        if (event.data.clearCookies) {
          document.cookie = 'hovrEngaged=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
      }
    },
  );
}

export default {
  fetchSiteInformation,
  addPostMessageListener: initPostMessageListener,
  getCookie,
  setCookie,
  getTopWindowURL,
  generateID,
  debugLog,
};
